// TODO: This service should be replaced !
import { Injectable, inject, signal } from '@angular/core';
import { Stripe } from '@stripe/stripe-js';
import { switchMap, take, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

import {
  IAdyenPaymentIntent,
  ILyfPayPaymentIntent,
  IPaygreenPaymentIntent,
  IStripePaymentIntent,
  IWorldlinePaymentIntent,
  IYavinPaymentIntent,
  IVivaWalletPaymentIntent,
  ILyfProAppPaymentIntent,
} from '@core/models/payment.model';

import { PaymentBancontactState } from '@store/payment/payment.state';
import { PaymentStoreDispatcher } from '@common/dispatchers/payment.dispatcher';

import { OrderPaymentsApiService } from '@core/services/api/order-payments/order-payments-api.service';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { IPaymentMethod, PaymentMethodHandles } from '@config/payment-methods.config';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { KioskHardwareType } from '@core/models/kiosk.model';
import { StripeLoaderService } from '@core/services/stripe-loader/stripe-loader.service';

/**
 * Handles that require Stripe.js (not Terminal SDK).
 * String literals avoid a circular import with payment-methods.config
 * (config → panels → PaymentService → config) at module init.
 */
const STRIPE_JS_HANDLES = new Set<string>([
  'STRIPE',
  'STRIPE_GOOGLE_PAY',
  'STRIPE_APPLE_PAY',
  'STRIPE_BANCONTACT',
  'STRIPE_TWINT',
]);

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  static stripeApplePayAvailable = false;
  static stripeGooglePayAvailable = false;

  pay$ = new Subject<boolean>();

  paymentIntent$ = new BehaviorSubject<IStripePaymentIntent | null>(null);
  paymentAdyenIntent$ = new BehaviorSubject<IAdyenPaymentIntent | null>(null);
  paymentYavinIntent$ = new BehaviorSubject<IYavinPaymentIntent | null>(null);
  paymentWorldlineIntent$ = new BehaviorSubject<IWorldlinePaymentIntent | null>(null);
  paymentVivaWalletIntent$ = new BehaviorSubject<IVivaWalletPaymentIntent | null>(null);
  paymentLyfpayIntent$ = new BehaviorSubject<ILyfPayPaymentIntent | null>(null);
  paymentLyfProAppIntent$ = new BehaviorSubject<ILyfProAppPaymentIntent | null>(null);
  paymentPaygreenIntent$ = new BehaviorSubject<IPaygreenPaymentIntent | null>(null);

  redirectUrl$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  isTransactionMandatory$ = new BehaviorSubject<boolean>(false);

  /**
   * Bumped when digital-wallet availability is known/updated so payment-method
   * lists can remap Apple/Google Pay after lazy Stripe load.
   */
  readonly digitalWalletsVersion = signal(0);
  readonly digitalWalletsVersion$ = new BehaviorSubject(0);

  private digitalWalletCheckStarted = false;

  private readonly stripeLoader = inject(StripeLoaderService);
  private readonly paymentDispatcher = inject(PaymentStoreDispatcher);
  private readonly settingsSelector = inject(SettingsStoreSelector);
  private readonly marketplaceSelector = inject(MarketplaceStoreSelector);
  private readonly orderPaymentService = inject(OrderPaymentsApiService);

  /** Loads Stripe.js only when a Stripe.js payment method is present. */
  prepareStripeForCheckout(methods: Array<{ handle: string }> | null | undefined): void {
    if (!methods?.some(method => STRIPE_JS_HANDLES.has(method.handle))) {
      return;
    }

    this.stripeLoader.ensureStripeReady$().pipe(take(1)).subscribe();
    this.checkDigitalWalletAvailable();
  }

  triggerPayment(): void {
    this.pay$.next(true);
  }

  public isUsablePaymentMethod(paymentMethod: IPaymentMethod): boolean {
    const kioskTerminalProvider = this.settingsSelector.kiosk.terminalProvider;

    if (
      paymentMethod.handle === PaymentMethodHandles.YavinTerminal &&
      kioskTerminalProvider !== KioskHardwareType.YAVIN_TERMINAL
    ) {
      return false;
    }

    if (
      paymentMethod.handle === PaymentMethodHandles.NeptingTerminal &&
      kioskTerminalProvider !== KioskHardwareType.NEPTING_TERMINAL
    ) {
      return false;
    }

    if (
      paymentMethod.handle === PaymentMethodHandles.TcposSco &&
      kioskTerminalProvider != null &&
      kioskTerminalProvider !== KioskHardwareType.TCPOS_SCO
    ) {
      return false;
    }

    // Viva Wallet Terminal is driven by the payment intent API, not by a local
    // reader. A missing or unrelated kiosk hardware must not hide the method:
    // the picker would stay on an empty choice screen and never auto-advance.

    // TODO: add check for Stripe Terminal, Adyen Terminal and AuresETK middleware

    return true;
  }

  public checkDigitalWalletAvailable(): void {
    if (this.digitalWalletCheckStarted) {
      return;
    }
    this.digitalWalletCheckStarted = true;

    this.stripeLoader
      .ensureStripeReady$()
      .pipe(
        switchMap(stripe =>
          this.marketplaceSelector.selectMarketplaceCurrency.pipe(
            take(1),
            tap(currency => this.resolveDigitalWallets(stripe, currency)),
          ),
        ),
        take(1),
      )
      .subscribe();
  }

  private resolveDigitalWallets(stripe: Stripe, currency: string): void {
    const paymentRequestOptions = {
      currency: currency.toLocaleLowerCase(),
      country: this.marketplaceSelector.marketplace.country.toUpperCase() || 'FR',
      total: {
        label: 'check',
        amount: 0,
      },
      requestPayerName: true,
      requestPayerEmail: true,
    };
    const paymentRequest = stripe.paymentRequest(paymentRequestOptions);
    void paymentRequest.canMakePayment().then(data => {
      PaymentService.stripeApplePayAvailable = Boolean(data?.applePay);
      PaymentService.stripeGooglePayAvailable = Boolean(data?.googlePay);
      this.bumpDigitalWalletsVersion();
    });
  }

  private bumpDigitalWalletsVersion(): void {
    const next = this.digitalWalletsVersion() + 1;
    this.digitalWalletsVersion.set(next);
    this.digitalWalletsVersion$.next(next);
  }

  setLoadingToStore(): void {
    this.paymentDispatcher.updateStatus({ isLoading: true });
  }

  setAdditionalData(bancontact: Partial<PaymentBancontactState>): void {
    this.paymentDispatcher.updateBancontact(bancontact);
  }

  unSetLoadingToStore(): void {
    this.paymentDispatcher.updateStatus({ isLoading: false });
  }

  createOrderPaymentIntent$<T>(orderId: string): Observable<T> {
    return this.orderPaymentService.createPaymentIntent$(orderId);
  }

  createTransactionPaymentIntent$<T>(orderId: string): Observable<T> {
    return this.orderPaymentService.createTransactionPaymentIntent$(orderId);
  }
}
